import React, { useEffect, useState } from 'react';
import { Breadcrumbs, Button, Slide } from '@mui/material';
import { Link, useLocation } from 'react-router-dom';
import MenuOpen from '@mui/icons-material/MenuOpenRounded';
import { replace_underscores_capitalize } from '../../../helpers/string_formatters';

import Snackbar from '@mui/material/Snackbar';
import { Alert } from '@mui/material';

import SimCardDownloadRoundedIcon from '@mui/icons-material/SimCardDownloadRounded';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import DateSelection from '../../../components/misc/DateSelection';
import { COLORS } from '../../../constants/colors';
import ModalIssueListDataLayer from '../data/ModalIssueListDataLayer';
import { useSelector } from 'react-redux';
import { GATrackDashboardLoad, EVENT_TYPES } from '../../../google_analytics/TrackEvent';
import { selectEmail, selectUserProductType } from '../../../store/reducers/userSlice';
import DashTable from '../../../components/generic-dashboards/DashTable';
import { entityCluster, entityRegistration } from '../../../interfaces/generic-entity.interface';
import { IssueActivity, IssueRegistrations } from '../properties/Tooltips';
import Cluster from '../../../components/generic-dashboards/Cluster';
import { issueInfo } from '../interface/issue-entity.interface';
import KeywordComposition from './KeywordComposition';
import IssueDashTableWithBullets from './DashTableWithBullets';
import TextSummary from 'features/shared/UI/exhibits/text-summary/TextSummary';
import { RangedSummary } from 'features/shared/interfaces/text-summary.interface';
import { DashboardDownloadModal } from 'components/misc/DashboardDownloadModal';
import {
    CorrelatedEntityCommunication,
    EntityCommunicationActivity,
    EntityCommunicationActivityBreakdown,
    EntityCommunicationMonthlyAggregate,
} from 'interfaces/entity-communication.interface';
import { ConfigDescriptor } from 'features/config/feature-configs/ConfigDescriptor';
import { EntityType } from 'features/config/Entities';
import { BarChartCollection } from 'features/shared/UI/exhibits/barchart/BarchartCollection';
import { BreakdownCollection } from 'features/shared/UI/exhibits/breakdown/BreakdownCollection';

const issueEntityDashboardConfig = ConfigDescriptor.getEntityConfig(EntityType.Issues);

interface clusterProps {
    cluster_data: entityCluster | null;
    title: string;
    tooltipContent: JSX.Element;
    loading: boolean;
}

interface dashboardProps {
    displayLobbyingData: boolean;
    setDisplayLobbyingData: (x: boolean) => void;
    date: string;
    handleDateChange: (x: string) => void;
    info: issueInfo | null;
    infoLoading: boolean;
    subjectMatterSummary: RangedSummary | null;
    subjectMatterSummaryLoading: boolean;
    updates: any | null;
    updatesLoading: boolean;
    overview: EntityCommunicationMonthlyAggregate[] | null;
    overviewLoading: boolean;
    recentLobbying: CorrelatedEntityCommunication[] | null;
    recentLobbyingLoading: boolean;
    recentRegistrations: entityRegistration[] | null;
    recentRegistrationsLoading: boolean;
    topSectorActivity: EntityCommunicationActivityBreakdown[] | null;
    topSectorActivityLoading: boolean;
    topEntities: {
        topSectors: EntityCommunicationActivity[] | null;
        topInstitutions: EntityCommunicationActivity[] | null;
        topFirms: EntityCommunicationActivity[] | null;
    };
    topEntitiesLoading: boolean;
    orgActivity: EntityCommunicationActivity[] | null;
    orgActivityLoading: boolean;
    dpohActivity: EntityCommunicationActivity[] | null;
    dpohActivityLoading: boolean;
    mpActivity: EntityCommunicationActivity[] | null;
    mpActivityLoading: boolean;
    clusters: clusterProps[];
    exportLoading: boolean;
    exportFail: boolean;
    isExportVisible?: boolean;
}

const IssueLobbyingDashboard = ({
    displayLobbyingData,
    setDisplayLobbyingData,
    date,
    handleDateChange,
    info = null,
    infoLoading = false,
    subjectMatterSummary = null,
    subjectMatterSummaryLoading = false,
    updates = null,
    updatesLoading = false,
    overview = null,
    overviewLoading = false,
    recentLobbying = [],
    recentLobbyingLoading = false,
    recentRegistrations = [],
    recentRegistrationsLoading = false,
    topSectorActivity = [],
    topSectorActivityLoading = false,
    clusters,
    topEntities = {
        topSectors: [],
        topInstitutions: [],
        topFirms: [],
    },
    topEntitiesLoading = false,
    orgActivity = [],
    orgActivityLoading = false,
    dpohActivity = [],
    dpohActivityLoading = false,
    mpActivity = [],
    mpActivityLoading = false,
    exportLoading = false,
    exportFail = false,
    isExportVisible = true,
}: dashboardProps) => {
    const type = 'issues';

    // Decide which color from MUI status color scheme to use for buttons
    const type_color = 'error';

    const overview_colors = COLORS[3];

    const [modalOpen, setModalOpen] = useState<boolean>(false);
    const [dateOpen, setDateOpen] = useState<boolean>(false);
    const [dateSet, setDateSet] = useState<boolean>(false);
    const [exportModalOpen, setExportModalOpen] = useState<boolean>(false);

    const handleOpen = () => setModalOpen(true);
    const handleClose = () => setModalOpen(false);

    const handleDateOpen = () => setDateOpen(true);
    const handleDateClose = () => {
        setDateOpen(false);
        setDateSet(true);
    };

    const [scrollPosition, setScrollPosition] = useState(0);

    const location = useLocation();
    const entity_name: string | null = new URLSearchParams(location.search).get('name');

    const userEmail = useSelector(selectEmail);
    const userProductType = useSelector(selectUserProductType);

    useEffect(() => {
        GATrackDashboardLoad(
            `${replace_underscores_capitalize(type)}`,
            `${entity_name}`,
            EVENT_TYPES.PAGE_LOAD,
            userEmail ? userEmail : undefined,
            userProductType ? userProductType : undefined
        );
        const handleScroll = () => {
            const scrollPosition = window.scrollY;
            setScrollPosition(scrollPosition);
        };
        window.addEventListener('scroll', handleScroll);
        // Cleanup the event listener when the component is unmounted
        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, [entity_name, userEmail, userProductType]);

    return (
        <div className="py-32 lg:px-20 px-4 grid grid-cols-12">
            <Snackbar
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                open={dateSet}
                onClose={() => setDateSet(false)}
                autoHideDuration={3000}
                message="Date Set"
            >
                <Alert onClose={() => setDateSet(false)} severity="success">
                    Date set to {date}
                </Alert>
            </Snackbar>

            <Slide direction="left" in={scrollPosition >= 50} mountOnEnter unmountOnExit>
                <div className="fixed z-20 bottom-36 right-5 gap-2 lg:top-20 lg:right-4 flex flex-col h-0">
                    <Button
                        className="group flex"
                        onClick={handleDateOpen}
                        variant="contained"
                        startIcon={<CalendarMonthRoundedIcon />}
                        color={type_color}
                        sx={{ backgroundColor: overview_colors.primary }}
                    >
                        {date}
                    </Button>
                    <Button
                        onClick={handleOpen}
                        variant="contained"
                        startIcon={<MenuOpen />}
                        color={type_color}
                        sx={{ backgroundColor: overview_colors.primary }}
                    >
                        {replace_underscores_capitalize(type)}
                    </Button>
                    {isExportVisible && (
                        <Button
                            variant="contained"
                            color={type_color}
                            sx={{ backgroundColor: overview_colors.primary }}
                            startIcon={<SimCardDownloadRoundedIcon />}
                            onClick={() => setExportModalOpen(true)}
                        >
                            Save PDF
                        </Button>
                    )}
                </div>
            </Slide>

            <ModalIssueListDataLayer open={modalOpen} handleClose={handleClose} />

            <DateSelection
                open={dateOpen}
                handleClose={handleDateClose}
                handleDateChange={handleDateChange}
                views={['year', 'month', 'day']}
            />

            {isExportVisible && (
                <DashboardDownloadModal
                    title={entity_name || 'Dashboard'}
                    type={type}
                    modalOpen={exportModalOpen}
                    handleClose={() => setExportModalOpen(false)}
                    updates={updates}
                    //overview={overview}
                    //keywordMentions={keywordMentions}
                    keywordComposition={info}
                    recentLobbying={[]}
                    recentRegistrations={recentRegistrations}
                    sectors={clusters[0].cluster_data}
                    organizations={clusters[1].cluster_data}
                    institutions={clusters[2].cluster_data}
                    publicOfficeHolders={clusters[3].cluster_data}
                    subjects={clusters[4].cluster_data}
                    lobbyFirms={clusters[5].cluster_data}
                    exportLoading={exportLoading}
                    exportFail={exportFail}
                />
            )}

            <div className="col-start-1 col-end-13 flex flex-row justify-between">
                <div>
                    <Breadcrumbs className="col-start-2 lg:col-end-12 col-end-12" separator=">">
                        <Link to="/" className="hover:underline">
                            Lobby<b>IQ</b>
                        </Link>
                        <Link to={`/${type}`} className="hover:underline">
                            {replace_underscores_capitalize(type)}
                        </Link>
                        <div>{entity_name}</div>
                    </Breadcrumbs>
                </div>
                <Slide direction="left" in={true}>
                    <div className="lg:flex hidden flex-row gap-4">
                        <Button
                            onClick={handleDateOpen}
                            variant="outlined"
                            startIcon={<CalendarMonthRoundedIcon />}
                            color={type_color}
                            sx={{ color: overview_colors.primary }}
                        >
                            Data From: {date}
                        </Button>
                        <Button
                            onClick={handleOpen}
                            variant="outlined"
                            startIcon={<MenuOpen />}
                            color={type_color}
                            sx={{ color: overview_colors.primary }}
                        >
                            {replace_underscores_capitalize(type)}
                        </Button>
                        {isExportVisible && (
                            <Button
                                variant="outlined"
                                color={type_color}
                                sx={{ color: overview_colors.primary }}
                                startIcon={<SimCardDownloadRoundedIcon />}
                                onClick={() => setExportModalOpen(true)}
                            >
                                Save PDF
                            </Button>
                        )}
                    </div>
                </Slide>
            </div>

            <div className="mt-4 col-start-1 col-end-13">
                <KeywordComposition
                    displayLobbyingData={displayLobbyingData}
                    setDisplayLobbyingData={setDisplayLobbyingData}
                    title={entity_name ?? 'Composition'}
                    subtitle=""
                    keywordData={info}
                    loading={infoLoading}
                />
            </div>

            <div className="mt-16 col-start-1 col-end-13">
                <TextSummary
                    title={`Summary of Recent Lobbying on ${entity_name ?? 'Issue'}`}
                    data={subjectMatterSummary}
                    loading={subjectMatterSummaryLoading}
                />
            </div>

            <div className="mt-16 col-start-1 col-end-13">
                <BreakdownCollection
                    dashboard={`${replace_underscores_capitalize(type)}`}
                    topic={`${entity_name}`}
                    title={`Top Sectors Lobbying on ${entity_name ?? 'Issue'}`}
                    subtitle="Communication Reports (12 months)"
                    dynamicData={[
                        {
                            title: 'Sectors Lobbying on Issue',
                            data: topEntities.topSectors ? topEntities.topSectors : null,
                            dataLoading: topEntitiesLoading,
                            hideData: true,
                            countKey: 'periodCount',
                            nameKey: 'entityName',
                            colorSet: COLORS[1],
                        },
                        {
                            title: 'Institutions Lobbied on Issue',
                            data: topEntities.topSectors ? topEntities.topInstitutions : null,
                            dataLoading: topEntitiesLoading,
                            hideData: true,
                            countKey: 'periodCount',
                            nameKey: 'entityName',
                            colorSet: COLORS[1],
                        },
                        {
                            title: 'GR Firms Lobbying on Issue',
                            data: topEntities.topSectors ? topEntities.topFirms : null,
                            dataLoading: topEntitiesLoading,
                            hideData: true,
                            countKey: 'periodCount',
                            nameKey: 'entityName',
                            colorSet: COLORS[1],
                        },
                    ]}
                />
            </div>

            <div className="mt-16 col-start-1 col-end-13">
                <BarChartCollection
                    dashboard={`${replace_underscores_capitalize(type)}`}
                    topic={`${entity_name}`}
                    title={`Top Sectors Lobbying on ${entity_name ?? 'Issue'}`}
                    subtitle="Communication Reports (12 months)"
                    dynamicData={[
                        {
                            title: 'All Sectors',
                            data: overview ? overview : null,
                            dataLoading: overviewLoading,
                            hideData: true,
                            xKey: 'mn',
                            colorSet: COLORS[1],
                        },
                        {
                            title: topSectorActivity?.[0]?.entity_name ?? '',
                            data: topSectorActivity?.[0]?.communicationBreakdown ?? null,
                            dataLoading: topSectorActivityLoading,
                            hideData: true,
                            xKey: 'mn',
                            colorSet: COLORS[0],
                        },
                        {
                            title: topSectorActivity?.[1]?.entity_name ?? '',
                            data: topSectorActivity?.[1]?.communicationBreakdown ?? null,
                            dataLoading: topSectorActivityLoading,
                            hideData: true,
                            xKey: 'mn',
                            colorSet: COLORS[2],
                        },
                        {
                            title: topSectorActivity?.[2]?.entity_name ?? '',
                            data: topSectorActivity?.[2]?.communicationBreakdown ?? null,
                            dataLoading: topSectorActivityLoading,
                            hideData: true,
                            xKey: 'mn',
                            colorSet: COLORS[3],
                        },
                    ]}
                />
            </div>

            <div className="mt-16 col-start-1 col-end-13">
                <IssueDashTableWithBullets
                    dashboard={`${replace_underscores_capitalize(type)}`}
                    topic={`${entity_name}`}
                    title={`Recent Lobbying on ${entity_name ?? 'issue'}`}
                    subtitle="Communication Reports (3 months)"
                    tableData={(recentLobbying || []).map((entry) => {
                        return {
                            ...entry,
                            'Prior Comms (12M)': entry.correlation,
                        };
                    })}
                    loading={recentLobbyingLoading}
                    tableHeader={[
                        'date',
                        'organization',
                        'dpoh',
                        'institution',
                        'sector',
                        'lobby_firm',
                        'consultant',
                        'Prior Comms (12M)',
                        'link',
                    ]}
                    tooltipContent={IssueActivity}
                    defaultNumRows={5}
                    updates={updates}
                    updatesLoading={updatesLoading}
                />
            </div>

            <div className="mt-16 col-start-1 col-end-13">
                <DashTable
                    dashboard={`${replace_underscores_capitalize(type)}`}
                    topic={`${entity_name}`}
                    title="Recent Lobbying Registrations with Issue Keywords"
                    tableData={recentRegistrations}
                    loading={recentRegistrationsLoading}
                    tableHeader={[
                        'date',
                        'organization',
                        'province',
                        'lobby_firm',
                        'consultant',
                        'history',
                        'link',
                    ]}
                    tooltipContent={IssueRegistrations}
                    defaultNumRows={5}
                />
            </div>

            <div className="mt-16 col-start-1 col-end-13">
                <DashTable
                    dashboard={`${replace_underscores_capitalize(type)}`}
                    topic={`${entity_name}`}
                    title={`Organizations Lobbying on ${entity_name ?? 'issue'}`}
                    subtitle="Communication Reports (12 months)"
                    tableData={(orgActivity || []).map((entry) => {
                        return {
                            ...entry,
                            name: entry.entity_name,
                            'prior comms on issue (12M)': entry.periodCount,
                            'Percent comms (12M)': entry.periodPercent,
                        };
                    })}
                    loading={orgActivityLoading}
                    tableHeader={[
                        'name',
                        'sector',
                        'prior comms on issue (12M)',
                        'Percent comms (12M)',
                    ]}
                    defaultNumRows={5}
                    defaultSort="prior comms on issue (12M)"
                    suffixes={{ 'Percent comms (12M)': '%' }}
                />
            </div>

            <div className="mt-16 col-start-1 col-end-13">
                <DashTable
                    dashboard={`${replace_underscores_capitalize(type)}`}
                    topic={`${entity_name}`}
                    title={`DPOHs Lobbied on ${entity_name ?? 'issue'}`}
                    subtitle="Communication Reports (12 months)"
                    tableData={(dpohActivity || []).map((entry) => {
                        return {
                            ...entry,
                            name: entry.entity_name,
                            'prior comms on issue (12M)': entry.periodCount,
                            'Percent comms (12M)': entry.periodPercent,
                        };
                    })}
                    loading={dpohActivityLoading}
                    tableHeader={[
                        'name',
                        'title',
                        'institution',
                        'prior comms on issue (12M)',
                        'Percent comms (12M)',
                    ]}
                    defaultNumRows={5}
                    defaultSort="prior comms on issue (12M)"
                    suffixes={{ 'Percent comms (12M)': '%' }}
                />
            </div>

            <div className="mt-16 col-start-1 col-end-13">
                <DashTable
                    dashboard={`${replace_underscores_capitalize(type)}`}
                    topic={`${entity_name}`}
                    title={`MPs Lobbied on ${entity_name ?? 'issue'}`}
                    subtitle="Communication Reports (12 months)"
                    tableData={(mpActivity || []).map((entry) => {
                        return {
                            ...entry,
                            name: entry.entity_name,
                            'prior comms on issue (12M)': entry.periodCount,
                            'Percent comms (12M)': entry.periodPercent,
                        };
                    })}
                    loading={mpActivityLoading}
                    tableHeader={[
                        'name',
                        'title',
                        'institution',
                        'prior comms on issue (12M)',
                        'Percent comms (12M)',
                    ]}
                    defaultNumRows={5}
                    defaultSort="prior comms on issue (12M)"
                    suffixes={{ 'Percent comms (12M)': '%' }}
                />
            </div>

            {issueEntityDashboardConfig.entityDashboardConfig.clusters.clustersVisible &&
                clusters
                    .filter((cluster: any) => cluster.title !== 'MP Mentions of issue')
                    .map((cluster, idx) => (
                        <div key={idx} className="mt-16 col-start-1 col-end-13">
                            <Cluster
                                dashboard={`${replace_underscores_capitalize(type)}`}
                                topic={`${entity_name}`}
                                title={cluster.title}
                                subtitle="Number of Communication Reports"
                                date={date}
                                clusterData={cluster.cluster_data}
                                tooltipContent={cluster.tooltipContent}
                                clusterLoading={cluster.loading}
                            />
                        </div>
                    ))}
        </div>
    );
};

export default IssueLobbyingDashboard;
